import { pruneObject } from 'cio/utils/objects';
import { Filter, Filters } from 'cio/utils/filters';
import {
  SerializedLegacyAttributeCompareFilter,
  SerializedLegacyAttributeFilter,
  SerializedLegacyFilter,
  SerializedLegacyForeignEventCondition,
  SerializedLegacyMessageFilter,
  SerializedLegacySegmentNegatableFilter,
} from 'cio/types/conditions/legacy-filter';
import flatMap from 'lodash.flatmap';
import { isSerializedSegmentNegatableCondition } from '../deserialize';
import namedBitmapToCondition from './named-bitmap';

// Interop between segments_negatable conditions and segment filters
// Segment filters are separate objects grouped by an "or" condition:
//   {
//     or: [
//       { segment: { id: 1 } },
//       { segment: { id: 2 } },
//     ],
//   }
//
// Segment negatable conditions, however, are grouped together into one condition:
//   { type: 'segments_negatable', segments: [1, 2] }
//
// This function is to be used by `reduce` to group segment filters together into a segments_negatable condition
const collectSegments = (agg: SerializedLegacyFilter[], condition: SerializedLegacyFilter) => {
  if (isSerializedSegmentNegatableCondition(condition)) {
    let existingSegmentNegatableIdx = agg.findIndex((otherCondition: SerializedLegacyFilter) => {
      return isSerializedSegmentNegatableCondition(otherCondition) && otherCondition.negate === condition.negate;
    });

    if (existingSegmentNegatableIdx >= 0) {
      (agg[existingSegmentNegatableIdx] as SerializedLegacySegmentNegatableFilter).segments.push(condition.segments[0]);

      return agg;
    }
  }

  return [...agg, condition];
};

function collectForeignEvent(agg: SerializedLegacyFilter[], filter: SerializedLegacyFilter): SerializedLegacyFilter[] {
  if (
    filter.type === 'foreign_event' &&
    'event_name' in filter &&
    filter.event_type != null &&
    (filter.event_type.includes('email') || filter.event_type.includes('action'))
  ) {
    return [
      ...agg,
      {
        event: {
          name: filter.event_name,
          type: filter.event_type,
        },
        inverse: false,
        type: 'message',
        isForeignEvent: true,
      } as SerializedLegacyMessageFilter,
    ];
  }
  if (filter.type === 'foreign_event' && 'event_name' in filter) {
    let existingForeignEventIndex = agg.findIndex((otherCondition) => {
      return (
        otherCondition.type === 'foreign_event' && 'event' in otherCondition && typeof otherCondition.event === 'object'
      );
    });

    if (existingForeignEventIndex >= 0) {
      throw new Error('Multiple foreign events are not supported');
    }

    let newFilter: SerializedLegacyForeignEventCondition = {
      type: 'foreign_event',
      inverse: false,
      event: {
        type: 'foreign_event',
        name: filter.event_name,
        filters: {
          and: [],
        },
      },
    };

    return [...agg, newFilter];
  }

  if (
    (filter.type === 'attribute_compare' && filter.field_source === 'foreign_event_attribute') ||
    filter.type === 'foreign_event_attribute'
  ) {
    // check an existing message condition first to merge the filter into it
    let existingMessageIndex = agg.findIndex((otherCondition) => {
      return otherCondition.type === 'message';
    });

    if (existingMessageIndex >= 0 && filter.type === 'foreign_event_attribute') {
      (agg[existingMessageIndex] as SerializedLegacyMessageFilter).event.filters = filter;
      return agg;
    }

    let existingForeignEventIndex = agg.findIndex((otherCondition) => {
      return otherCondition.type === 'foreign_event';
    });

    if (existingForeignEventIndex === -1) {
      throw new Error('attribute_compare or foreign_event_attribute filter must follow a foreign event filter');
    }

    (agg[existingForeignEventIndex] as any).event?.filters?.and?.push(filter);
    return agg;
  }

  return [...agg, filter];
}

export default function deserializeFilters(
  filter: SerializedLegacyFilter[] | Filters | Filter,
  not = false,
  agg: SerializedLegacyFilter[] = [],
  level = 0,
): SerializedLegacyFilter[] {
  if (Array.isArray(filter)) {
    return filter.reduce(collectForeignEvent, []);
  }

  if ('attribute' in filter) {
    let { attribute } = filter;

    return [
      ...agg,
      pruneObject({
        type: 'attribute',
        operator: `${not ? '!' : ''}${attribute.operator}`,
        field: attribute.field,
        value: attribute.value,
      }) as SerializedLegacyAttributeFilter,
    ];
  } else if ('object_attribute' in filter) {
    let { object_attribute } = filter;

    return [
      ...agg,
      pruneObject({
        type: 'object_attribute',
        operator: `${not ? '!' : ''}${object_attribute.operator}`,
        field: object_attribute.field,
        value: object_attribute.value,
        type_id: object_attribute.type_id,
      }) as SerializedLegacyAttributeFilter,
    ];
  } else if ('attribute_compare' in filter) {
    let { attribute_compare } = filter;

    return [
      ...agg,
      pruneObject({
        type: 'attribute_compare',
        operator: `${not ? '!' : ''}${attribute_compare.operator}`,
        field: attribute_compare.field,
        field_source: attribute_compare.field_source,
        value: attribute_compare.value,
        comparison_operator: `${attribute_compare.value_source}`,
        value2: attribute_compare.value2,
      }) as SerializedLegacyAttributeCompareFilter,
    ];
  } else if ('device_attribute' in filter) {
    let { device_attribute: attribute } = filter;

    return [
      ...agg,
      pruneObject({
        type: 'device_attribute',
        operator: `${not ? '!' : ''}${attribute.operator}`,
        field: attribute.field,
        value: attribute.value,
      }) as SerializedLegacyAttributeFilter,
    ];
  } else if ('segment' in filter) {
    return [...agg, { type: 'segments_negatable', negate: not, segments: [filter.segment.id] }];
  } else if ('named_bitmap' in filter) {
    return [...agg, namedBitmapToCondition(filter, not)];
  } else if ('newsletter_recipients' in filter) {
    return [
      ...agg,
      {
        type: 'newsletter_recipients',
        newsletter_id: filter.newsletter_recipients.id,
        negate: not,
      },
    ];
  } else if ('not' in filter) {
    let { not } = filter;
    let nots = deserializeFilters(not, true, [], level + 1).map((not) => {
      if (isSerializedSegmentNegatableCondition(not)) {
        return { ...not, negate: true };
      }

      return not;
    });

    return [...agg, ...nots];
  } else if ('and' in filter) {
    let ands = flatMap(filter.and, (and) => deserializeFilters(and, not, [], level + 1));

    if (level === 0) {
      return ands;
    }

    return [...agg, ...ands.reduce(collectSegments, [])];
  } else if ('or' in filter) {
    let ors = flatMap(filter.or, (or) => deserializeFilters(or, not, [], level + 1));

    if (level === 0) {
      return ors;
    }

    return [...agg, ...ors.reduce(collectSegments, [])];
  } else if ('map' in filter) {
    let map = deserializeFilters(filter.map, not, [], level);

    if (level === 0) {
      return map;
    }

    return [...agg, ...map];
  } else if ('foreign_event' in filter) {
    let foreignEvent = deserializeFilters(filter.foreign_event, not, [], level);

    if (level === 0) {
      return foreignEvent;
    }

    return [...agg, ...foreignEvent];
  }
  return [];
}
